import React, { useState, useEffect, useContext } from 'react';
import {
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Grid,
  Typography,
  Box,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VariantCreateDialog from 'components/popups/VariantCreateDialog';
import VariantLabelCreateDialog from 'components/popups/VariantLabelCreateDialog';
import DataTable from 'components/utils/DataTable';
import VariantService from 'services/VariantService';
import styles from './styles.module.css';
import ConfirmPopup from 'components/utils/Alert/ConfirmPopup';
import VariantEditDialog from 'components/popups/VariantEditDialog';
import VariantLabelEditDialog from 'components/popups/VariantLabelEditDialog';
import Info from 'components/utils/Alert/Info';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { InventoryContext } from 'Context/InventoryContext';
import { AlertMessagesContext } from 'react-alert-messages';
import { useTranslation } from 'react-i18next';
import { UserContext } from 'Context/UserContext';
import { ShopContext } from 'Context/ShopContext';
import { SUBSCRIPTION_TYPE, USER_PERMISSIONS } from 'const';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';

export default function VariantConfig() {
  const { t: tCommon } = useTranslation('common');
  const { t: tManagement } = useTranslation('management');
  const { user } = useContext(UserContext);
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const {
    variantLabels,
    setVariantLabels,
    variantDimensions,
    setVariantDimensions,
    getVariantLabels,
    getVariantDimensions,
  } = useContext(InventoryContext);

  const [isLoading, setLoading] = useState(false);

  const [isVariantDialogOpen, setVariantDialogOpen] = useState(false);
  const [isVariantLabelDialogOpen, setVariantLabelDialogOpen] = useState(false);

  const [variantToEdit, setVariantToEdit] = useState(null);
  const [labelToEdit, setLabelToEdit] = useState(null);

  const [isEditDialogOpen, setEditDialogOpen] = useState(false);
  const [isLabelEditDialogOpen, setLabelEditDialogOpen] = useState(false);

  const [variantToDelete, setVariantToDelete] = useState(null);
  const [confirmVariantOpen, setConfirmVariantOpen] = useState(false);

  const [labelToDelete, setLabelToDelete] = useState(null);
  const [confirmLabelOpen, setConfirmLabelOpen] = useState(false);

  const [expandedVariantId, setExpandedVariantId] = useState(false);
  const [initialVariantId, setInitialVariantId] = useState(null);

  const subscriptionType = shop?.subscription?.type;
  const userHasSalesPersonWritePermission = user?.shop_permissions?.sales_persons_permission === USER_PERMISSIONS.WRITE;

  const handleVariantUpdateSuccess = (updatedVariant) => {
    setVariantDimensions((prev) => prev.map((v) => (v.id === updatedVariant.id ? updatedVariant : v)));
    setVariantToEdit(null);
    setEditDialogOpen(false);
  };

  const handleVariantDelete = async () => {
    if (!variantToDelete) return;
    setLoading(true);
    try {
      await VariantService.deleteVariantDimension(variantToDelete.id);
      setVariantDimensions((prev) => prev.filter((v) => v.id !== variantToDelete.id));
      setVariantLabels((prev) => prev.filter((l) => l.dimension?.id !== variantToDelete.id));
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoading(false);
    setVariantToDelete(null);
    setConfirmVariantOpen(false);
  };

  const handleLabelUpdateSuccess = (updatedLabel) => {
    setVariantLabels((prev) => prev.map((l) => (l.id === updatedLabel.id ? updatedLabel : l)));
    setLabelToEdit(null);
    setLabelEditDialogOpen(false);
  };

  const handleLabelDelete = async () => {
    if (!labelToDelete) return;
    setLoading(true);
    try {
      await VariantService.deleteVariantLabel(labelToDelete.id);
      setVariantLabels((prev) => prev.filter((l) => l.id !== labelToDelete.id));
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoading(false);
    setLabelToDelete(null);
    setConfirmLabelOpen(false);
  };

  const handleAccordionChange = (variantId) => (event, isExpanded) => {
    setExpandedVariantId(isExpanded ? variantId : false);
  };

  const labelHeaderData = [
    {
      label: tCommon('Label Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Actions'),
      id: 'actions',
      type: 'callback',
      viewRender: (item) => (
        <Grid className={styles.btnGroup}>
          <Tooltip
            title={tCommon('Edit Label')}
            onClick={() => {
              setLabelToEdit(item);
              setLabelEditDialogOpen(true);
            }}
          >
            <EditIcon className={styles.iconButton} />
          </Tooltip>
          <Tooltip
            title={tCommon('Delete Label')}
            onClick={() => {
              setLabelToDelete(item);
              setConfirmLabelOpen(true);
            }}
          >
            <DeleteIcon className={styles.iconButton} />
          </Tooltip>
        </Grid>
      ),
    },
  ];

  useEffect(() => {
    getVariantLabels();
  }, [getVariantLabels]);

  useEffect(() => {
    getVariantDimensions();
  }, [getVariantDimensions]);

  return (
    <div className={styles.contentLayout}>
      <div className={styles.settingsGrid}>
        <div className={styles.BillHeaderDiv}>
          <span className={styles.pageHead}>{tCommon('Manage Variant Dimensions')}</span>
          <div className={styles.filerInputSec}>
            {subscriptionType &&
              subscriptionType === SUBSCRIPTION_TYPE.PREMIUM &&
              userHasSalesPersonWritePermission && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  style={{ backgroundColor: '#ff851b' }}
                  className={styles.actionBtn}
                  onClick={() => setVariantDialogOpen(true)}
                >
                  <NoteAddIcon className={styles.actionBtnIcon} />
                  {tCommon('New Variant Dimensions')}
                </Button>
              )}
          </div>
        </div>

        {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.PREMIUM ? (
          <RestrictionInfo
            title={tManagement('Varients feature not available')}
            content={tManagement('To get Varients support options, contact support team.')}
          />
        ) : (
          <div>
            {variantDimensions && variantDimensions.length === 0 ? (
              <Info
                title={tManagement('No Variant Dimensions')}
                content={tManagement('Please create a new variant dimension.')}
              />
            ) : (
              <>
                <Box className={styles.tableHead}>
                  <Box sx={{ width: 40 }} />
                  <Typography className={styles.headerName}>{tCommon('Variant Name')}</Typography>
                  <Typography className={styles.headerAction}>{tCommon('Actions')}</Typography>
                </Box>

                {(variantDimensions || []).map((variant) => {
                  const labelsForVariant = (variantLabels || []).filter((l) => l.dimension?.id === variant.id);
                  return (
                    <Accordion
                      key={variant.id}
                      expanded={expandedVariantId === variant.id}
                      onChange={handleAccordionChange(variant.id)}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          flexDirection: 'row-reverse',
                          '& .MuiAccordionSummary-content': {
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          },
                        }}
                        aria-controls={`panel-${variant.id}-content`}
                        id={`panel-${variant.id}-header`}
                        style={{ width: '100%' }}
                      >
                        <div>
                          <Typography sx={{ flex: 1 }} className={styles.variantName}>
                            {variant.name}
                          </Typography>
                        </div>
                        <div>
                          <Tooltip title={tCommon('Edit Variant')}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setVariantToEdit(variant);
                                setEditDialogOpen(true);
                              }}
                              style={{ paddingRight: '10px', color: 'rgb(128, 128, 128)' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={tCommon('Delete Variant')}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setVariantToDelete(variant);
                                setConfirmVariantOpen(true);
                              }}
                              style={{ paddingRight: '10px', color: 'rgb(128, 128, 128)' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={tCommon('Create Variant Label')}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setVariantLabelDialogOpen(true);
                                setInitialVariantId(variant.id);
                              }}
                              style={{ paddingRight: '16px', color: 'rgb(128, 128, 128)' }}
                            >
                              <AddCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </AccordionSummary>
                      <AccordionDetails>
                        {labelsForVariant && labelsForVariant.length ? (
                          <div className={styles.myDataTable}>
                            <DataTable rows={labelsForVariant} columns={labelHeaderData} />
                          </div>
                        ) : (
                          <Info title={tManagement('No Labels')} content={tManagement('No labels under this variant. Create one.')} />
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>

      <VariantCreateDialog
        open={isVariantDialogOpen}
        handleClose={() => setVariantDialogOpen(false)}
        handleSuccess={(createdVariant) => setVariantDimensions((prev) => [...prev, createdVariant])}
      />

      <VariantLabelCreateDialog
        open={isVariantLabelDialogOpen}
        variantOptions={variantDimensions}
        initialVariantId={initialVariantId}
        handleClose={() => {
          setVariantLabelDialogOpen(false);
          setInitialVariantId(null);
        }}
        handleSuccess={(createdLabel) => {
          setVariantLabels((prev) => [...prev, createdLabel]);
          setVariantLabelDialogOpen(false);
          setInitialVariantId(null);
        }}
      />

      <ConfirmPopup
        data={confirmVariantOpen}
        handleClose={() => {
          setVariantToDelete(null);
          setConfirmVariantOpen(false);
        }}
        handleOkay={handleVariantDelete}
        alertTitle={tManagement('Delete Variant Dimension?')}
        alertContent={tManagement('Are you sure you want to delete variant "{{name}}"?', { name: variantToDelete?.name })}
        successBtnName={tCommon('Yes, delete')}
        isLoading={isLoading}
      />
      <ConfirmPopup
        data={confirmLabelOpen}
        handleClose={() => {
          setLabelToDelete(null);
          setConfirmLabelOpen(false);
        }}
        handleOkay={handleLabelDelete}
        alertTitle={tManagement('Delete Variant Label?')}
        alertContent={tManagement('Are you sure you want to delete label "{{name}}"?', { name: labelToDelete?.name })}
        successBtnName={tCommon('Yes, delete')}
        isLoading={isLoading}
      />

      <VariantEditDialog
        variant={variantToEdit}
        open={isEditDialogOpen}
        handleClose={() => {
          setVariantToEdit(null);
          setEditDialogOpen(false);
        }}
        handleSuccess={handleVariantUpdateSuccess}
      />
      <VariantLabelEditDialog
        label={labelToEdit}
        open={isLabelEditDialogOpen}
        variantOptions={variantDimensions}
        handleClose={() => {
          setLabelToEdit(null);
          setLabelEditDialogOpen(false);
        }}
        handleSuccess={handleLabelUpdateSuccess}
      />
    </div>
  );
}
